import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit, Trash2, Copy, Search, FileText, Clock, TrendingUp, X, Save } from 'lucide-react'
import api from '../../services/api'
import toast from 'react-hot-toast'

const getTemplates = async () => {
  const response = await api.get('/api/email-templates')
  return response.data
}

const createTemplate = async (data) => {
  const response = await api.post('/api/email-templates', data)
  return response.data
}

const updateTemplate = async ({ id, data }) => {
  const response = await api.put(`/api/email-templates/${id}`, data)
  return response.data
}

const deleteTemplate = async (id) => {
  const response = await api.delete(`/api/email-templates/${id}`)
  return response.data
}

const duplicateTemplate = async (id) => {
  const response = await api.post(`/api/email-templates/${id}/duplicate`)
  return response.data
}

export default function EmailTemplates() {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subject: '',
    body: '',
    category: ''
  })

  // Récupérer les templates
  const { data, isLoading } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: getTemplates
  })

  const templates = data?.templates || []

  // Filtrer les templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = searchQuery === '' || 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase()))
    
    const matchesCategory = selectedCategory === '' || template.category === selectedCategory
    
    return matchesSearch && matchesCategory
  })

  // Catégories uniques
  const categories = [...new Set(templates.filter(t => t.category).map(t => t.category))]

  // Mutations
  const createMutation = useMutation({
    mutationFn: createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(['emailTemplates'])
      toast.success('Template créé !')
      resetForm()
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Erreur lors de la création')
    }
  })

  const updateMutation = useMutation({
    mutationFn: updateTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(['emailTemplates'])
      toast.success('Template mis à jour !')
      resetForm()
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(['emailTemplates'])
      toast.success('Template supprimé !')
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression')
    }
  })

  const duplicateMutation = useMutation({
    mutationFn: duplicateTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(['emailTemplates'])
      toast.success('Template dupliqué !')
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Erreur lors de la duplication')
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!formData.name || !formData.subject || !formData.body) {
      toast.error('Nom, sujet et corps sont requis')
      return
    }

    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleEdit = (template) => {
    setEditingTemplate(template)
    setFormData({
      name: template.name,
      description: template.description || '',
      subject: template.subject,
      body: template.body,
      category: template.category || ''
    })
    setIsCreating(true)
  }

  const handleDelete = (id, name) => {
    if (confirm(`Supprimer le template "${name}" ?`)) {
      deleteMutation.mutate(id)
    }
  }

  const handleDuplicate = (id) => {
    duplicateMutation.mutate(id)
  }


  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      subject: '',
      body: '',
      category: ''
    })
    setIsCreating(false)
    setEditingTemplate(null)
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
        <div className="spinning" style={{ 
          width: 48, height: 48, 
          border: '4px solid rgba(91, 92, 244,0.1)', 
          borderTop: '4px solid #5b5cf4', 
          borderRadius: '50%', 
          margin: '0 auto 1rem',
          animation: 'spin 1s linear infinite' 
        }}></div>
        Chargement des templates...
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            Templates d'emails
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
            Créez et réutilisez vos emails avec variables personnalisables
          </p>
        </div>
        
        <button
          onClick={() => setIsCreating(true)}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#5b5cf4',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem'
          }}
        >
          <Plus size={18} />
          Nouveau template
        </button>
      </div>

      {/* Barre de recherche et filtres */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        {/* Recherche */}
        <div style={{ flex: 1, position: 'relative' }}>
          <Search
            size={20}
            style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-text-secondary)'
            }}
          />
          <input
            type="text"
            placeholder="Rechercher un template..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              background: 'rgb(var(--rgb-ink) / 0.05)',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '8px',
              padding: '0.75rem 1rem 0.75rem 3rem',
              color: 'var(--color-text)',
              fontSize: '0.875rem'
            }}
          />
        </div>

        {/* Filtre catégorie */}
        {categories.length > 0 && (
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              background: 'rgb(var(--rgb-ink) / 0.05)',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              color: 'var(--color-text)',
              fontSize: '0.875rem',
              cursor: 'pointer'
            }}
          >
            <option value="">Toutes catégories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        )}
      </div>

      {/* Formulaire création/édition */}
      {isCreating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgb(var(--rgb-shade) / 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '2rem'
        }} onClick={resetForm}>
          <div
            style={{
              background: 'rgb(var(--rgb-surface-hover))',
              borderRadius: '12px',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: '2rem'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                {editingTemplate ? 'Modifier le template' : 'Nouveau template'}
              </h3>
              <button
                onClick={resetForm}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Nom */}
              <div>
                <label style={{ display: 'block', color: 'var(--color-text)', fontSize: '0.875rem', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Nom du template <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Premier contact"
                  required
                  style={{
                    width: '100%',
                    background: 'rgb(var(--rgb-ink) / 0.05)',
                    border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                    borderRadius: '6px',
                    padding: '0.75rem',
                    color: 'var(--color-text)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Ex: Email pour premier contact prospects France"
                  style={{
                    width: '100%',
                    background: 'rgb(var(--rgb-ink) / 0.05)',
                    border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                    borderRadius: '6px',
                    padding: '0.75rem',
                    color: 'var(--color-text)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Catégorie */}
              <div>
                <label style={{ display: 'block', color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                  Catégorie
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ex: Prospection, Follow-up, etc."
                  style={{
                    width: '100%',
                    background: 'rgb(var(--rgb-ink) / 0.05)',
                    border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                    borderRadius: '6px',
                    padding: '0.75rem',
                    color: 'var(--color-text)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Sujet */}
              <div>
                <label style={{ display: 'block', color: 'var(--color-text)', fontSize: '0.875rem', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Sujet <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Bonjour {{prenom}}, découvrez notre solution"
                  required
                  style={{
                    width: '100%',
                    background: 'rgb(var(--rgb-ink) / 0.05)',
                    border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                    borderRadius: '6px',
                    padding: '0.75rem',
                    color: 'var(--color-text)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Corps */}
              <div>
                <label style={{ display: 'block', color: 'var(--color-text)', fontSize: '0.875rem', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Corps du message <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="Bonjour {{prenom}},&#10;&#10;Je me permets de vous contacter..."
                  required
                  rows={10}
                  style={{
                    width: '100%',
                    background: 'rgb(var(--rgb-ink) / 0.05)',
                    border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                    borderRadius: '6px',
                    padding: '0.75rem',
                    color: 'var(--color-text)',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                <div style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                  Variables disponibles : {'{{prenom}}'}, {'{{nom}}'}, {'{{entreprise}}'}, {'{{poste}}'}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'rgb(var(--rgb-ink) / 0.05)',
                    border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                    borderRadius: '6px',
                    color: 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#5b5cf4',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Save size={16} />
                  {editingTemplate ? 'Mettre à jour' : 'Créer le template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Liste des templates */}
      {filteredTemplates.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: 'rgb(var(--rgb-ink) / 0.02)',
          borderRadius: '12px',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)'
        }}>
          <FileText size={48} style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }} />
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem' }}>
            {searchQuery || selectedCategory ? 'Aucun template trouvé' : 'Aucun template créé'}
          </p>
          {!searchQuery && !selectedCategory && (
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Créez votre premier template pour gagner du temps
            </p>
          )}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredTemplates.map(template => (
            <div
              key={template.id}
              style={{
                background: 'rgb(var(--rgb-ink) / 0.02)',
                border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '12px',
                padding: '1.5rem',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.05)'
                e.currentTarget.style.borderColor = 'rgba(91, 92, 244,0.3)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.02)'
                e.currentTarget.style.borderColor = 'rgb(var(--rgb-ink) / 0.1)'
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ 
                    fontSize: '1rem', 
                    fontWeight: 600,
                    marginBottom: '0.25rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {template.name}
                  </h3>
                  <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                    {template.shared && (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        background: 'rgb(var(--rgb-aqua) / 0.12)',
                        color: 'rgb(var(--rgb-aqua))',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}>
                        Partagé
                      </span>
                    )}
                    {template.type === 'relance' && (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(251,146,60,0.12)',
                        color: '#fb923c',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}>
                        Relance
                      </span>
                    )}
                    {template.category && (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(91, 92, 244,0.1)',
                        color: '#5b5cf4',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}>
                        {template.category}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {template.description && (
                <p style={{
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.8125rem',
                  marginBottom: '1rem',
                  lineHeight: 1.5
                }}>
                  {template.description}
                </p>
              )}

              {/* Sujet */}
              <div style={{
                background: 'rgb(var(--rgb-shade) / 0.2)',
                padding: '0.75rem',
                borderRadius: '6px',
                marginBottom: '1rem'
              }}>
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  Sujet:
                </div>
                <div style={{
                  color: 'var(--color-text)',
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {template.subject}
                </div>
              </div>

              {/* Stats */}
              <div style={{
                display: 'flex',
                gap: '1rem',
                fontSize: '0.75rem',
                color: 'var(--color-text-muted)',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <TrendingUp size={14} />
                  {template.usageCount} utilisations
                </div>
                {template.lastUsedAt && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Clock size={14} />
                    {new Date(template.lastUsedAt).toLocaleDateString('fr-FR')}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid rgb(var(--rgb-ink) / 0.05)'
              }}>
                {!template.shared && (
                  <button
                    onClick={() => handleEdit(template)}
                    style={{
                      padding: '0.5rem',
                      background: 'rgb(var(--rgb-ink) / 0.05)',
                      border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                      borderRadius: '6px',
                      color: 'var(--color-text-secondary)',
                      cursor: 'pointer'
                    }}
                    title="Modifier"
                  >
                    <Edit size={16} />
                  </button>
                )}
                <button
                  onClick={() => handleDuplicate(template.id)}
                  style={{
                    padding: '0.5rem',
                    background: 'rgb(var(--rgb-ink) / 0.05)',
                    border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                    borderRadius: '6px',
                    color: 'var(--color-text-secondary)',
                    cursor: 'pointer'
                  }}
                  title="Dupliquer"
                >
                  <Copy size={16} />
                </button>
                {!template.shared && (
                  <button
                    onClick={() => handleDelete(template.id, template.name)}
                    style={{
                      padding: '0.5rem',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      borderRadius: '6px',
                      color: '#ef4444',
                      cursor: 'pointer'
                    }}
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Compteur */}
      {filteredTemplates.length > 0 && (
        <div style={{
          marginTop: '2rem',
          textAlign: 'center',
          color: 'var(--color-text-muted)',
          fontSize: '0.875rem'
        }}>
          {filteredTemplates.length} template{filteredTemplates.length > 1 ? 's' : ''} affiché{filteredTemplates.length > 1 ? 's' : ''}
          {(searchQuery || selectedCategory) && templates.length > filteredTemplates.length && (
            <span> sur {templates.length} au total</span>
          )}
        </div>
      )}
    </div>
  )
}
